/**
 * pop_up_show / pop_up_click 公共业务附加字段解析
 *
 * - tab_name：按 pathname 映射 discover / my_list / profile / reader
 * - book_id / chapter_id：仅阅读器页有值时附带（store 优先，URL 兜底）
 */

import { ROUTER } from '@/constants/router';
import { useReaderStore } from '@/store/reader';

/** pop_up_show 约定的 tab_name */
export type TPopupShowTabName = 'discover' | 'my_list' | 'profile' | 'reader';

/** 去掉末尾 `/`（根路径除外），便于与 ROUTER 常量精确匹配 */
function normalizePathname(pathname: string): string {
    const p = (pathname || '/').toLowerCase();
    if (p.length > 1 && p.endsWith('/')) {
        return p.slice(0, -1);
    }
    return p;
}

/**
 * pathname → pop_up_show.tab_name
 * @param pathname 当前路由；缺省取 window.location.pathname
 */
export function resolvePopupShowTabName(pathname?: string): TPopupShowTabName | undefined {
    const raw = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
    const normalized = normalizePathname(raw);

    if (normalized === '/' || normalized === '' || normalized === ROUTER.DISCOVER) {
        return 'discover';
    }
    if (normalized === ROUTER.MY_LIST) {
        return 'my_list';
    }
    if (normalized === ROUTER.PROFILE) {
        return 'profile';
    }
    if (normalized === ROUTER.READER) {
        return 'reader';
    }
    return undefined;
}

/**
 * 阅读器内 book_id / chapter_id；非阅读器或不存在时不返回对应字段
 * @param pathname 当前路由；缺省取 window.location.pathname
 */
export function resolvePopupShowReaderIds(pathname?: string): {
    book_id?: string;
    chapter_id?: string;
} {
    const raw = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
    if (normalizePathname(raw) !== ROUTER.READER) {
        return {};
    }

    const readerState = useReaderStore.getState();
    const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    // 埋点 book_id 与阅读器 drama_id 对齐；优先 store（滚动后章节可能已变）
    const bookId = readerState.dramaId || search?.get('drama_id') || search?.get('book_id') || '';
    const chapterId = readerState.anchorChapterId || readerState.chapterId || search?.get('chapter_id') || '';

    const result: { book_id?: string; chapter_id?: string } = {};
    if (bookId) {
        result.book_id = bookId;
    }
    if (chapterId) {
        result.chapter_id = chapterId;
    }
    return result;
}

/**
 * 组装弹窗埋点页面上下文附加字段（tab_name + 可选阅读器 id）
 */
export function resolvePopupShowTrackParams(pathname?: string): Record<string, string> {
    const params: Record<string, string> = {};
    const tabName = resolvePopupShowTabName(pathname);
    if (tabName) {
        params.tab_name = tabName;
    }
    Object.assign(params, resolvePopupShowReaderIds(pathname));
    return params;
}
